import React, { useState, useEffect, useCallback, useMemo } from "react";
import { X } from "lucide-react";

// Slugify a section title into a stable anchor id.
const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

// A line is a top-level section header if it's all-caps (with spaces, & or -)
// and not blank. The "SECTIONS" header is special and acts as a TOC slot.
const HEADER_REGEX = /^[A-Z][A-Z\s&-]*$/;
// A short, title-cased line (no sentence punctuation) is treated as a subheader.
const SUBHEADER_REGEX = /^[A-Z][a-zA-Z0-9\s&/()-]+$/;

const Manual = ({ showManual, onClose }) => {
  const [manualContent, setManualContent] = useState("");
  const [manualLoading, setManualLoading] = useState(false);

  const loadManual = useCallback(async () => {
    if (manualContent) return;
    setManualLoading(true);
    try {
      const response = await fetch("/MANUAL.txt");
      if (response.ok) {
        const content = await response.text();
        setManualContent(content);
      } else {
        setManualContent("Manual content could not be loaded.");
      }
    } catch (error) {
      console.error("Failed to load manual:", error);
      setManualContent("Error loading manual content.");
    }
    setManualLoading(false);
  }, [manualContent]);

  useEffect(() => {
    if (showManual && !manualContent) {
      loadManual();
    }
  }, [showManual, manualContent, loadManual]);

  // Walk the content once to collect every top-level section header. The
  // TOC links to every header except SECTIONS itself (which is the TOC slot)
  // and the two foundational sections that already sit above the TOC.
  const tocEntries = useMemo(() => {
    if (!manualContent) return [];
    const skip = new Set(["SECTIONS", "GETTING STARTED", "FLOATING NOTEPAD"]);
    return manualContent
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => HEADER_REGEX.test(line) && line.length > 0)
      .filter((line) => !skip.has(line))
      .map((title) => ({ title, id: `manual-${slugify(title)}` }));
  }, [manualContent]);

  // Smooth-scroll a TOC click to the matching anchor without polluting the URL.
  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!showManual) return null;

  return (
    <div className="rounded-lg border p-6 mb-6 transition-colors bg-forest-800 border-forest-600">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-forest-100">User Manual</h2>
        <button
          onClick={onClose}
          className="transition-colors text-forest-300 hover:text-forest-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {manualLoading ? (
        <div className="text-center py-8 text-forest-300">
          Loading manual...
        </div>
      ) : (
        <div className="whitespace-pre-line leading-relaxed text-forest-200">
          {manualContent.split("\n").map((line, index) => {
            const trimmed = line.trim();

            // The SECTIONS header is the TOC slot — render the table of
            // contents in place of the bare header line.
            if (trimmed === "SECTIONS") {
              return (
                <div key={index} className="mt-6 mb-3">
                  <div className="font-bold text-lg mb-2 text-forest-100">
                    Sections
                  </div>
                  <ul
                    className="list-none p-0 m-0"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: "0.25rem 1rem",
                    }}
                  >
                    {tocEntries.map((entry) => (
                      <li key={entry.id}>
                        <a
                          href={`#${entry.id}`}
                          onClick={(e) => handleTocClick(e, entry.id)}
                          className="text-gold-400 hover:text-gold-300 hover:underline"
                        >
                          {entry.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            const isHeader = HEADER_REGEX.test(trimmed) && trimmed.length > 0;
            const isSubHeader =
              !isHeader &&
              trimmed.length > 0 &&
              trimmed.length < 60 &&
              SUBHEADER_REGEX.test(trimmed);

            if (isHeader) {
              const id = `manual-${slugify(trimmed)}`;
              return (
                <div
                  key={index}
                  id={id}
                  className="font-bold text-lg mt-6 mb-3 text-forest-100"
                  style={{ scrollMarginTop: "5rem" }}
                >
                  {line}
                </div>
              );
            }
            if (isSubHeader) {
              return (
                <div
                  key={index}
                  className="font-semibold mt-4 mb-2 text-forest-100"
                >
                  {line}
                </div>
              );
            }
            return <div key={index}>{line}</div>;
          })}
        </div>
      )}
    </div>
  );
};

export default Manual;
